import { parseDeepLinkParams } from '../hooks/useNavigationPersistence';

/**
 * Maps a saved screen to the module's root/dashboard on reload.
 * Detail/redirect screens depend on transient state that is lost on hard refresh,
 * so we always restore the module entry point instead.
 */
export const RELOAD_MODULE_ROOT: Record<string, string> = {
  ShiftHandoverRedirect: "Shift Handover",
  LeadershipWalkRedirect: "Leadership Walk",
  PulseCheck: "Observations",
  EmployeeUpdate: "Observations",
  "NHO Admin": "NHO",
  "NHO Manage Employees": "NHO",
  NHOEmployeeProfile: "NHO",
  "NHO Begin": "NHO",
  "NHO PulseCheckTouchPoint": "NHO",
  "NHO SpecificPeriodTouchPoint": "NHO",
  "PeopleHub Activity": "People Hub",
  "PeopleHub Observation": "People Hub",
  DLOLocationSetting: "DLODashboard",
  DLOCreateSchedule: "DLODashboard",
  DLOScheduleSetting: "DLODashboard",
  "Submit Ticket": "Ticketing Portal",
  "My Tickets": "Ticketing Portal",
  "Sustain Queue": "Ticketing Portal",
  "Ticket Detail": "Ticketing Portal",
  "Notifications & Comms": "Ticketing Portal",
  "Team Management": "Ticketing Portal",
};

/**
 * Set of screens that can be restored from persistent storage.
 * Only screens in this set are considered valid restore targets.
 */
export const RESTORABLE_SCREENS = new Set([
  "Home", "Observations", "Shift Handover", "Employee Update", "Leadership Walk",
  "SPARC Walk", "Racking Inspection", "Contact us/Feedback", "Quality Audit",
  "Admin Dashboard", "Site Evaluation", "Action Plan", "Stand Up", "NHO",
  "Analytics", "Perfect Truck",
  "/navigator-reports/perfect-truck-analytics", "/navigator-reports/on-time-dispatch",
  "Ticketing Analytics Report",
  "DLODashboard", "Labor Optimization", "Ticketing Portal", "Submit Ticket",
  "My Tickets", "Sustain Queue", "Ticket Detail", "Notifications & Comms", "Team Management",
  "People Hub",
  "Harbor", "Delivery RPM", "Perfect Truck",
]);

/**
 * Resolves a raw saved screen string to a valid screen with params.
 * Handles:
 * - Module root mapping: detail screens redirect to their module dashboard
 * - Query param parsing: extracts params from deep links
 * - Screen validation: ensures only whitelisted screens can be restored
 */
export const resolveRestoreScreen = (raw: string): { screen: string; params: Record<string, string> } | null => {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "Home") {
    return null;
  }

  // Parse screen name and query params (e.g., "Ticket Detail?ticketId=NTI-2026-00022")
  const { screen, params } = parseDeepLinkParams(trimmed);

  // Preserve shared deep links for specific tickets.
  // Example: "Ticket Detail?ticketId=NTI-2026-00022" should open that ticket directly,
  // while plain Ticketing Portal sub-screen reloads still map to module root.
  if (screen === "Ticket Detail" && typeof params.ticketId === "string" && params.ticketId.trim()) {
    return { screen, params };
  }

  // Preserve shared deep links for a specific notification (e.g. a Teams "View Details"
  // link), so reloading the same URL re-surfaces the same pre-expanded message.
  if (screen === "Ticketing Portal" && typeof params.notificationId === "string" && params.notificationId.trim()) {
    return { screen, params: { ...params, isDeepLinked: "true" } };
  }

  // Navigator report screens have path-like names (e.g. /navigator-reports/perfect-truck-analytics).
  // They are always restorable — no module-root mapping needed.
  if (screen.startsWith("/navigator-reports/")) {
    return { screen, params };
  }

  const mapped = RELOAD_MODULE_ROOT[screen] ??
    (screen.startsWith("DLO") ? "DLODashboard" :
     screen.startsWith("Labor Optimization") ? "Labor Optimization" : screen);

  return RESTORABLE_SCREENS.has(mapped) ? { screen: mapped, params } : null;
};
